import { useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Checkbox,
  Drawer,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconBan,
  IconCheck,
  IconPlus,
  IconRotateClockwise,
  IconSearch,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatMoney, MEASURE_METRIC_INFO, MEASURE_METRICS, type MeasureMetric } from '@erp/shared';
import { ApiError } from '../api/client';
import { unitsOfMeasureApi, vatRatesApi } from '../api/catalogs';
import {
  priceListItemsApi,
  priceListSectionsApi,
  type PriceListItem,
  type PriceListItemInput,
} from '../api/priceList';
import { buildSectionOptions, PriceListSectionTree } from '../components/PriceListSectionTree';
import { RegistryPage } from '../components/RegistryPage';
import { useAuth } from '../auth/AuthContext';

const emptyForm: PriceListItemInput = {
  name: '',
  code: '',
  sectionId: '',
  unitId: '',
  vatRateId: '',
  price: '',
  measureMetric: null,
  wastePercent: '',
  isActive: true,
};

const metricOptions = MEASURE_METRICS.map((m) => ({
  value: m,
  label: `${MEASURE_METRIC_INFO[m].label}, ${MEASURE_METRIC_INFO[m].unit}`,
}));

export function PriceListPage() {
  const { hasRole } = useAuth();
  const canEdit = hasRole('ADMIN', 'ACCOUNTANT', 'MANAGER');
  const queryClient = useQueryClient();

  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PriceListItemInput>(emptyForm);
  const [formError, setFormError] = useState<string[] | null>(null);

  const sections = useQuery({
    queryKey: ['price-list-sections'],
    queryFn: () => priceListSectionsApi.list(),
  });
  const units = useQuery({ queryKey: ['units-of-measure'], queryFn: () => unitsOfMeasureApi.list() });
  const vatRates = useQuery({ queryKey: ['vat-rates'], queryFn: () => vatRatesApi.list() });

  const list = useQuery({
    queryKey: ['price-list-items', { search: debouncedSearch, sectionId: selectedSectionId, includeInactive }],
    queryFn: () =>
      priceListItemsApi.listAll({
        search: debouncedSearch,
        sectionId: selectedSectionId ?? undefined,
        includeInactive,
      }),
  });

  const unitOptions = (units.data?.items ?? []).map((u) => ({ value: u.id, label: `${u.symbol} — ${u.name}` }));
  const vatRateOptions = (vatRates.data?.items ?? []).map((r) => ({ value: r.id, label: r.name }));
  const sectionOptions = [{ value: '', label: 'Без раздела' }, ...buildSectionOptions(sections.data?.items ?? [])];

  function openCreate() {
    setEditingId(null);
    setForm({ ...emptyForm, sectionId: selectedSectionId ?? '' });
    setFormError(null);
    setDrawerOpen(true);
  }

  function openEdit(item: PriceListItem) {
    setEditingId(item.id);
    setForm({
      name: item.name,
      code: item.code ?? '',
      sectionId: item.sectionId ?? '',
      unitId: item.unitId,
      vatRateId: item.vatRateId ?? '',
      price: item.price,
      measureMetric: item.measureMetric,
      wastePercent: item.wastePercent ?? '',
      isActive: item.isActive,
    });
    setFormError(null);
    setDrawerOpen(true);
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: PriceListItemInput = {
        ...form,
        code: form.code || null,
        sectionId: form.sectionId || null,
        vatRateId: form.vatRateId || null,
        measureMetric: form.measureMetric || null,
        wastePercent: form.measureMetric ? form.wastePercent || null : null,
      };
      return editingId ? priceListItemsApi.update(editingId, payload) : priceListItemsApi.create(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['price-list-items'] });
      notifications.show({ color: 'green', message: 'Расценка сохранена' });
      setDrawerOpen(false);
    },
  });

  function handleSubmit() {
    setFormError(null);
    if (!form.name.trim()) {
      setFormError(['Укажите наименование']);
      return;
    }
    if (!form.unitId) {
      setFormError(['Укажите единицу измерения']);
      return;
    }
    if (!form.price.trim()) {
      setFormError(['Укажите цену']);
      return;
    }
    saveMutation.mutate(undefined, {
      onError: (err) => {
        setFormError(err instanceof ApiError ? (err.details ?? [err.message]) : ['Не удалось сохранить']);
      },
    });
  }

  const toggleActiveMutation = useMutation({
    mutationFn: (item: PriceListItem) =>
      item.isActive ? priceListItemsApi.deactivate(item.id) : priceListItemsApi.activate(item.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['price-list-items'] });
    },
  });

  const createSection = useMutation({
    mutationFn: priceListSectionsApi.create,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['price-list-sections'] }),
  });
  const renameSection = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => priceListSectionsApi.update(id, { name }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['price-list-sections'] }),
  });
  const deleteSection = useMutation({
    mutationFn: (id: string) => priceListSectionsApi.remove(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['price-list-sections'] }),
  });

  const items = list.data ?? [];

  return (
    <RegistryPage
      title="Расценки"
      action={
        canEdit && (
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            Создать расценку
          </Button>
        )
      }
      filters={
        <Group>
          <TextInput
            placeholder="Поиск по наименованию или коду"
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={320}
          />
          <Checkbox
            label="Показывать отключённые"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.currentTarget.checked)}
          />
        </Group>
      }
      /* Дерево разделов — часть навигации по справочнику, а не строка списка:
         прокручивается независимо от таблицы расценок. */
      aside={
        <PriceListSectionTree
          sections={sections.data?.items ?? []}
          selectedId={selectedSectionId}
          onSelect={setSelectedSectionId}
          canEdit={canEdit}
          onCreate={(data) => createSection.mutateAsync(data).then(() => undefined)}
          onRename={(id, name) => renameSection.mutateAsync({ id, name }).then(() => undefined)}
          onDelete={(id) => deleteSection.mutateAsync(id).then(() => undefined)}
        />
      }
    >
      <Table striped highlightOnHover verticalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Наименование</Table.Th>
            <Table.Th>Код</Table.Th>
            <Table.Th>Раздел</Table.Th>
            <Table.Th>Ед. изм.</Table.Th>
            <Table.Th>Ставка НДС</Table.Th>
            <Table.Th>Цена</Table.Th>
            <Table.Th>Статус</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((item) => (
            <Table.Tr key={item.id} style={{ cursor: 'pointer' }} onClick={() => openEdit(item)}>
              <Table.Td>
                <Text size="sm" fw={500}>
                  {item.name}
                </Text>
              </Table.Td>
              <Table.Td>{item.code ?? '—'}</Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed">
                  {item.section?.name ?? '—'}
                </Text>
              </Table.Td>
              <Table.Td>{item.unit.symbol}</Table.Td>
              <Table.Td>{item.vatRate?.name ?? '—'}</Table.Td>
              <Table.Td>{formatMoney(item.price)}</Table.Td>
              <Table.Td>
                <Badge color={item.isActive ? 'green' : 'gray'} variant="light">
                  {item.isActive ? 'Активна' : 'Отключена'}
                </Badge>
              </Table.Td>
              <Table.Td onClick={(e) => e.stopPropagation()}>
                {canEdit && (
                  <Tooltip label={item.isActive ? 'Отключить' : 'Включить'}>
                    <ActionIcon
                      variant="subtle"
                      color={item.isActive ? 'red' : 'green'}
                      onClick={() => toggleActiveMutation.mutate(item)}
                    >
                      {item.isActive ? <IconBan size={16} /> : <IconRotateClockwise size={16} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {!list.isLoading && items.length === 0 && (
        <Text c="dimmed" size="sm" ta="center" py="xl">
          Расценки не найдены
        </Text>
      )}

      <Drawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editingId ? 'Расценка' : 'Новая расценка'}
        position="right"
        size="lg"
        padding="lg"
      >
        <Stack gap="sm">
          {formError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              <Stack gap={2}>
                {formError.map((e, i) => (
                  <Text key={i} size="sm">
                    {e}
                  </Text>
                ))}
              </Stack>
            </Alert>
          )}

          <Group grow>
            <TextInput
              label="Наименование"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.currentTarget.value })}
              required
              disabled={!canEdit}
            />
            <TextInput
              label="Код"
              value={form.code ?? ''}
              onChange={(e) => setForm({ ...form, code: e.currentTarget.value })}
              disabled={!canEdit}
            />
          </Group>

          <Select
            label="Раздел"
            data={sectionOptions}
            value={form.sectionId || ''}
            onChange={(v) => setForm((f) => ({ ...f, sectionId: v ?? '' }))}
            disabled={!canEdit}
            searchable
          />

          <Group grow>
            <Select
              label="Единица измерения"
              placeholder="Выберите единицу"
              data={unitOptions}
              value={form.unitId || null}
              onChange={(v) => setForm((f) => ({ ...f, unitId: v ?? '' }))}
              disabled={!canEdit}
              required
              searchable
            />
            <Select
              label="Ставка НДС"
              placeholder="Не указана"
              data={vatRateOptions}
              value={form.vatRateId || ''}
              onChange={(v) => setForm((f) => ({ ...f, vatRateId: v ?? '' }))}
              disabled={!canEdit}
              clearable
            />
          </Group>

          <TextInput
            label="Цена за единицу без НДС"
            placeholder="0.00"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.currentTarget.value })}
            disabled={!canEdit}
            required
          />

          <Group grow align="flex-start">
            <Select
              label="Объём по обмерам"
              description="Подставится в смету автоматически"
              placeholder="Вводить вручную"
              data={metricOptions}
              value={form.measureMetric ?? null}
              onChange={(v) => setForm((f) => ({ ...f, measureMetric: (v as MeasureMetric | null) ?? null }))}
              disabled={!canEdit}
              clearable
            />
            <TextInput
              label="Запас, %"
              description="На подрезку и отходы"
              placeholder="0"
              inputMode="decimal"
              value={form.wastePercent ?? ''}
              onChange={(e) => setForm({ ...form, wastePercent: e.currentTarget.value })}
              disabled={!canEdit || !form.measureMetric}
            />
          </Group>

          {canEdit && (
            <Button
              leftSection={<IconCheck size={16} />}
              onClick={handleSubmit}
              loading={saveMutation.isPending}
              mt="xs"
            >
              Сохранить
            </Button>
          )}
        </Stack>
      </Drawer>
    </RegistryPage>
  );
}
